import React, { useState } from 'react';
import { useErrorLogs, type ErrorLog } from '@/hooks/useErrorLogs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Eye, ChevronDown, AlertTriangle, ChevronRight } from 'lucide-react';
import ScrollArea from '@/components/ui/shared/ScrollArea';
import { getFriendlyError } from '@/lib/friendlyErrors';
import { SettingsCard } from './SettingsCard';

const SOURCE_OPTIONS = [
  { value: 'all', label: 'Todas as origens' },
  { value: 'processamento-ia', label: 'Processamento IA' },
];

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function StatusBadge({ status }: { status: number | null }) {
  if (!status) return <span className="text-[#64748B] dark:text-[#71717A]">-</span>;

  if (status >= 200 && status < 300) {
    return <Badge className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">{status}</Badge>;
  }
  if (status >= 400 && status < 500) {
    return <Badge className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800">{status}</Badge>;
  }
  return <Badge className="bg-red-50 text-[#DC2626] border-red-200 dark:bg-red-900/30 dark:text-[#F87171] dark:border-red-800">{status}</Badge>;
}

function buildFriendlyFromLog(log: ErrorLog) {
  return getFriendlyError({
    errorCode: log.error_code,
    errorMessage: log.error_message,
    details: log.details,
    httpStatus: log.http_status,
  });
}

const LogsTab: React.FC = () => {
  const { logs, loading, hasMore, sourceFilter, fetchLogs, loadMore, changeSourceFilter } = useErrorLogs();
  const [selectedLog, setSelectedLog] = useState<ErrorLog | null>(null);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  const selectedFriendly = selectedLog ? buildFriendlyFromLog(selectedLog) : null;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">Logs de Erros</h2>
          <p className="text-sm text-[#64748B] dark:text-[#71717A]">Erros registrados nas integrações da conta</p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={sourceFilter || 'all'}
            onValueChange={(val) => changeSourceFilter(val === 'all' ? null : val)}
          >
            <SelectTrigger className="w-[200px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]">
              <SelectValue placeholder="Filtrar por origem" />
            </SelectTrigger>
            <SelectContent className="rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA]">
              {SOURCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLogs}
            disabled={loading}
            className="rounded-xl border border-[#E5E7EB] text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] dark:border-[#3F3F464D] dark:text-[#A1A1AA] dark:hover:text-[#FAFAFA] dark:hover:bg-[#3F3F46]"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <SettingsCard>
        {loading && logs.length === 0 ? (
          <div className="p-8 text-center text-[#64748B] dark:text-[#71717A]">Carregando...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-[#64748B] dark:text-[#71717A]">Nenhum erro registrado</div>
        ) : (
          <>
            <ScrollArea classNames={{ root: 'max-h-[500px] overflow-auto' }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white dark:bg-[#27272A] z-10">
                  <tr className="border-b border-[#E5E7EB] dark:border-[#3F3F464D] text-[#64748B] dark:text-[#71717A] text-left">
                    <th className="px-4 py-3 font-medium w-[155px]">Data/Hora</th>
                    <th className="px-4 py-3 font-medium w-[140px]">Origem</th>
                    <th className="px-4 py-3 font-medium w-[200px]">Código</th>
                    <th className="px-4 py-3 font-medium">Mensagem</th>
                    <th className="px-4 py-3 font-medium w-[70px] text-center">Status</th>
                    <th className="px-4 py-3 font-medium w-[60px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const friendly = buildFriendlyFromLog(log);
                    return (
                      <tr
                        key={log.id}
                        className="border-b border-[#E5E7EB] dark:border-[#3F3F464D] hover:bg-[#F8FAFC] transition-colors duration-200 dark:hover:bg-[#18181B]/50"
                      >
                        <td className="px-4 py-3 text-[#475569] dark:text-[#A1A1AA] text-xs whitespace-nowrap">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="default2" className="text-xs">
                            {log.source}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-[#334155] dark:text-[#D4D4D8] font-mono text-xs">
                          {log.error_code || '-'}
                        </td>
                        <td className="px-4 py-3 text-[#334155] dark:text-[#D4D4D8] max-w-[300px]">
                          <div className="truncate font-medium" title={friendly.title}>
                            {friendly.title}
                          </div>
                          <div className="truncate text-xs text-[#64748B] dark:text-[#71717A]" title={friendly.description}>
                            {friendly.description}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <StatusBadge status={log.http_status} />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => {
                              setSelectedLog(log);
                              setShowTechnicalDetails(false);
                            }}
                            className="flex justify-center items-center rounded-[8px] border border-[#E5E7EB] dark:border-[#3F3F464D] bg-[#F1F5F9] dark:bg-[#27272A] w-[30px] h-[30px] hover:bg-[#F1F5F9] dark:hover:bg-[#3F3F46] transition-colors"
                          >
                            <Eye size={14} className="text-[#334155] dark:text-[#D4D4D8]" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollArea>

            {hasMore && (
              <div className="p-3 text-center border-t border-[#E5E7EB] dark:border-[#3F3F464D]">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadMore}
                  disabled={loading}
                  className="text-[#64748B] hover:text-[#0F172A] dark:text-[#71717A] dark:hover:text-[#FAFAFA]"
                >
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Carregar mais
                </Button>
              </div>
            )}
          </>
        )}
      </SettingsCard>

      <Dialog
        open={!!selectedLog}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedLog(null);
            setShowTechnicalDetails(false);
          }
        }}
      >
        <DialogContent className="flex w-full p-0 rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA] flex-col max-w-[600px]">
          <ScrollArea classNames={{ root: 'max-h-[80dvh] p-5 overflow-auto' }}>
            <DialogHeader>
              <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">Detalhes do Erro</DialogTitle>
            </DialogHeader>

            {selectedLog && selectedFriendly && (
              <div className="mt-4 space-y-4">
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="text-[#DC2626] dark:text-[#F87171] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                        {selectedFriendly.title}
                      </p>
                      <p className="text-xs text-red-600/90 mt-1 whitespace-pre-wrap break-words dark:text-red-200/90">
                        {selectedFriendly.description}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#64748B] dark:text-[#71717A] uppercase">Data/Hora</label>
                    <p className="text-[#334155] dark:text-[#D4D4D8] text-sm">{formatDate(selectedLog.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-xs text-[#64748B] dark:text-[#71717A] uppercase">Origem</label>
                    <p className="text-[#334155] dark:text-[#D4D4D8] text-sm">{selectedLog.source}</p>
                  </div>
                  <div>
                    <label className="text-xs text-[#64748B] dark:text-[#71717A] uppercase">Status HTTP</label>
                    <div className="mt-1"><StatusBadge status={selectedLog.http_status} /></div>
                  </div>
                  <div>
                    <label className="text-xs text-[#64748B] dark:text-[#71717A] uppercase">Código</label>
                    <p className="text-[#334155] dark:text-[#D4D4D8] text-sm font-mono">{selectedLog.error_code || '-'}</p>
                  </div>
                </div>

                <div className="border border-[#E5E7EB] dark:border-[#3F3F464D] rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowTechnicalDetails((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors duration-200 dark:bg-[#18181B]/40 dark:hover:bg-[#18181B]/70"
                  >
                    <span className="text-xs uppercase tracking-wide text-[#64748B] dark:text-[#71717A] font-medium">
                      Detalhes técnicos
                    </span>
                    <ChevronRight
                      size={16}
                      className={`text-[#64748B] dark:text-[#71717A] transition-transform ${
                        showTechnicalDetails ? 'rotate-90' : ''
                      }`}
                    />
                  </button>

                  {showTechnicalDetails && (
                    <div className="p-4 space-y-3 border-t border-[#E5E7EB] dark:border-[#3F3F464D]">
                      <div>
                        <label className="text-xs text-[#64748B] dark:text-[#71717A] uppercase">Mensagem original</label>
                        <p className="text-[#334155] dark:text-[#D4D4D8] text-sm mt-1 break-words">
                          {selectedLog.error_message}
                        </p>
                      </div>

                      {selectedLog.details && (
                        <div>
                          <label className="text-xs text-[#64748B] dark:text-[#71717A] uppercase">Detalhes</label>
                          <pre className="mt-1 text-xs text-[#475569] dark:text-[#A1A1AA] bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all dark:bg-[#18181B] dark:border-[#3F3F46]">
                            {selectedLog.details}
                          </pre>
                        </div>
                      )}

                      {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                        <div>
                          <label className="text-xs text-[#64748B] dark:text-[#71717A] uppercase">Metadata</label>
                          <pre className="mt-1 text-xs text-[#475569] dark:text-[#A1A1AA] bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all dark:bg-[#18181B] dark:border-[#3F3F46]">
                            {JSON.stringify(selectedLog.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LogsTab;
